import * as React from 'react';

import * as api from '@gitbook/api-client';
import { DateShortRelative, IntlPluralize } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    DataTable,
    type DataTableColumns,
    DataTableFooter,
    DataTableLayout,
    DataTableRoot,
    Description,
    formatChartNumber,
    Heading,
    Link,
    Pagination,
    type PaginationNavigationCallbacks,
    Panel,
    PanelHeader,
    Tooltip,
} from '@gitbook/spine-react';

import {
    SpaceLinkSuspense,
    TranslationControlButton,
    TranslationLanguageSpan,
    TranslationStatusBadge,
} from '../../../Components';
import { createDocumentationURL } from '../../../utils';

export function TranslationsTable(
    props: {
        organizationId: string;
        translations: api.Translation[];
    } & PaginationNavigationCallbacks
) {
    const { organizationId, translations, ...paginationProps } = props;

    const columns = React.useMemo<DataTableColumns<api.Translation>>(() => {
        return [
            {
                key: 'status',
                label: 'Status',
                cell: ({ item }) => <TranslationStatusBadge translation={item} />,
            },
            {
                key: 'source',
                label: 'Source',
                cell: ({ item }) => <SpaceLinkSuspense spaceId={item.source.space} />,
            },
            {
                key: 'translation',
                label: 'Translation',
                isRowHeader: true,
                cell: ({ item }) => (
                    <div className="flex flex-row items-center gap-2">
                        <TranslationLanguageSpan language={item.source.language} />
                        <div>
                            <Icon.ArrowRight className="size-4 text-secondary" />
                        </div>
                        <TranslationLanguageSpan language={item.language} />
                    </div>
                ),
            },
            {
                key: 'runs',
                label: 'Runs',
                cell: ({ item }) => <>{item.stats.runs}</>,
            },
            {
                key: 'updatedAt',
                label: 'Last run',
                cell: ({ item }) =>
                    item.stats.runs > 0 ? (
                        <>
                            <DateShortRelative value={item.updatedAt} onlyDate={false} />
                            {item.status === api.TranslationStatus.Completed ? (
                                <>
                                    {' '}
                                    in{' '}
                                    <span className="text-secondary">
                                        {item.stats.latestRunDuration}s
                                    </span>
                                </>
                            ) : null}
                        </>
                    ) : (
                        <div className="text-muted">{'-'}</div>
                    ),
            },
            {
                key: 'pages',
                label: 'Pages',
                cell: ({ item }) =>
                    item.stats.latestPages > 0 ? (
                        <Tooltip
                            label={`Total of ${formatChartNumber(item.stats.totalPages)} over all runs and an average of ${formatChartNumber(Math.round(item.stats.totalPages / item.stats.runs))} per run.`}
                        >
                            <span className="underline decoration-dotted">
                                {formatChartNumber(item.stats.latestPages)}{' '}
                                <IntlPluralize
                                    count={item.stats.latestPages}
                                    singular="page"
                                    plural="pages"
                                />
                            </span>
                        </Tooltip>
                    ) : (
                        <div className="text-muted">{'-'}</div>
                    ),
            },
            {
                key: 'words',
                label: 'Words',
                cell: ({ item }) =>
                    item.stats.latestWords > 0 ? (
                        <Tooltip
                            label={`Total of ${formatChartNumber(item.stats.totalWords)} over all runs and an average of ${formatChartNumber(Math.round(item.stats.totalWords / item.stats.runs))} per run.`}
                        >
                            <span className="underline decoration-dotted">
                                {formatChartNumber(item.stats.latestWords)}{' '}
                                <IntlPluralize
                                    count={item.stats.latestWords}
                                    singular="word"
                                    plural="words"
                                />
                            </span>
                        </Tooltip>
                    ) : (
                        <div className="text-muted">{'-'}</div>
                    ),
            },
            {
                key: 'actions',
                label: '',
                width: 64,
                cell: ({ item }) =>
                    item.permissions.edit ? (
                        <TranslationControlButton
                            organizationId={organizationId}
                            translation={item}
                        />
                    ) : null,
            },
        ];
    }, [organizationId]);

    return (
        <Panel>
            <PanelHeader>
                <Heading>Translation workflows</Heading>
                <Description>
                    Edit and manage your configured translation workflows. Workflows will re-run
                    everytime source content is updated.{' '}
                    <Link
                        href={createDocumentationURL('creating-content/translations')}
                        target="_blank"
                    >
                        Learn more
                    </Link>
                </Description>
            </PanelHeader>
            <DataTableRoot>
                <DataTableLayout>
                    <DataTable<api.Translation>
                        columns={columns}
                        items={translations}
                        aria-label="Table"
                    />
                    <DataTableFooter>
                        <Pagination {...paginationProps} />
                    </DataTableFooter>
                </DataTableLayout>
            </DataTableRoot>
        </Panel>
    );
}
